import {log} from "../../logging/logEvent";
import {observeApi} from "../../apiObservables";
import {Clock} from "js-joda";

const AUTHENTICATION_FAILED = "AUTHENTICATION_FAILED";

const typePattern = /^(SUBSCRIPTION_[A-Z_]+)\/(.+)/;

function subscriptionReducer(state = {}, action) {
    switch (action.type) {
        case "SUBSCRIPTION_STARTED":
            return { ...state, url: action.url, staleAfter: action.staleAfter };
        case "SUBSCRIPTION_DATA_ARRIVED":
            return { ...state, lastSuccess: action.timeStamp, isStale: false };
        case "SUBSCRIPTION_DATA_FAILED":
            if (state && state.lastSuccess && (action.timeStamp - state.lastSuccess >= state.staleAfter)) {
                return { ...state, isStale: true };
            }
            return state;
        case "SUBSCRIPTION_INITIAL_DATA_FAILED":
            return { ...state, hasFailed: true };
        case "SUBSCRIPTION_ENDED":
            return {};
        default:
            return state;
    }
}

export function reducer(state = {}, action) {
    const match = typePattern.exec(action.type);
    if (match) {
        const subtype = match[1];
        const name = match[2];
        return { ...state, [name]: subscriptionReducer(state[name], { ...action, type: subtype })};
    }
    else {
        return state;
    }
}

export function subscriptionStarted(name, url, initialWait, staleAfterSeconds) {
    return {type: `SUBSCRIPTION_STARTED/${name}`, name, url, initialWait, staleAfter: staleAfterSeconds * 1000};
}

export function subscriptionDataArrived(name, payload, userIsWaiting, startTime, fetchRequestFamilyId, timestamp = new Date()) {
    return {type: `SUBSCRIPTION_DATA_ARRIVED/${name}`, name, payload, timeStamp: timestamp, userIsWaiting, startTime, fetchRequestFamilyId};
}

export function authenticationFailed() {
    return {type: AUTHENTICATION_FAILED};
}

export function subscriptionInitialDataFailed(name, code, timestamp = new Date()) {
    return {type: `SUBSCRIPTION_INITIAL_DATA_FAILED/${name}`, name, payload: code, timeStamp: timestamp};
}

export function subscriptionDataFailed(name, code, timestamp = new Date()) {
    return {type: `SUBSCRIPTION_DATA_FAILED/${name}`, name, payload: code, timeStamp: timestamp};
}

export function subscriptionEnded(name) {
    return {type: `SUBSCRIPTION_ENDED/${name}`, name};
}

export function subscriptionKicked(name) {
    return {type: `SUBSCRIPTION_KICKED/${name}`, name};
}

function timeElapsedSince(start, clock = Clock.systemDefaultZone()) {
    return clock.millis() - start;
}

function observeApiAsActions(url, name, intervalSeconds, initialWaitMillis = 0, staleAfterSeconds = 5 * 60, clock = Clock.systemDefaultZone()) {
    return observeApi(url, intervalSeconds, initialWaitMillis, clock).map(
        ({ data, error, code, xhr, startTime, isInitial }) => {
            if (!error) {
                return subscriptionDataArrived(name, data, isInitial, startTime, xhr.getResponseHeader("X-TIM-Request-Family-Id"));
            }
            else if (code === 403) {
                return authenticationFailed();
            }
            else if (isInitial) {
                return subscriptionInitialDataFailed(name, code);
            }
            else {
                return subscriptionDataFailed(name, code);
            }
        }
    );
}

export function logDataFetchedMiddleware(clock = Clock.systemDefaultZone()) {
    return store => next => action => {
        if (action.type.startsWith("SUBSCRIPTION_DATA_ARRIVED/")) {
            const { startTime, userIsWaiting, fetchRequestFamilyId, name } = action;
            const fetchDuration = timeElapsedSince(startTime, clock);

            next(action);

            const { url } = store.getState().subscriptions[name];

            log(`${name}_DataFetched`, {
                url,
                fetchTime: fetchDuration,
                processingTime: timeElapsedSince(startTime + fetchDuration, clock),
                totalTime: timeElapsedSince(startTime, clock),
                userIsWaiting,
                fetchRequestFamilyId
            })
        }
        else {
            next(action);
        }
    };
}

const subscriptions = {};

class SubscriptionListeners {
    constructor() {
        this._listeners = {};
    }

    addListener(name) {
        return new Promise((resolve, reject) => {
            let list = this._listeners[name];
            if (!list) {
                list = [];
                this._listeners[name] = list;
            }
            list.push({ resolve, reject });
        });
    }

    fireResolve(name, value) {
        let list = this._listeners[name];
        if (!list) return;
        list.forEach(listener => {
            listener.resolve(value);
        });
        delete this._listeners[name];
    }

    fireReject(name, value) {
        let list = this._listeners[name];
        if (!list) return;
        list.forEach(listener => {
            listener.reject(value);
        });
        delete this._listeners[name];
    }

    // for testing
    reset() {
        this._listeners = [];
    }
}

const subscriptionListeners = new SubscriptionListeners();

export function subscriptionDataArrivedMiddleware() {
    return store => next => action => {
        next(action);
        if (action.type.startsWith("SUBSCRIPTION_DATA_ARRIVED/")) {
            const { name } = action;
            subscriptionListeners.fireResolve(name, true);
        }
        else if (action.type.startsWith("SUBSCRIPTION_INITIAL_DATA_FAILED/") || action.type.startsWith("SUBSCRIPTION_DATA_FAILED/")) {
            const { name } = action;
            subscriptionListeners.fireReject(name, new Error(action.type));
        }
    }
}

// for testing
subscriptionDataArrivedMiddleware.reset = () => {
    subscriptionListeners.reset();
};

export default dispatch => ({
    subscribe(url, name, intervalSeconds, initialWaitMillis = 0, staleAfterSeconds = 5 * 60, clock = Clock.systemDefaultZone()) {
        dispatch(subscriptionStarted(name, url, initialWaitMillis, staleAfterSeconds));

        let subscription = observeApiAsActions(url, name, intervalSeconds, initialWaitMillis, staleAfterSeconds, clock).subscribe(dispatch);

        const wrappedSubscription = {
            unsubscribe() {
                subscription.unsubscribe();
                subscriptions[name] = null;
                dispatch(subscriptionEnded(name));
            },

            kick() {
                subscription.unsubscribe();
                subscription = observeApiAsActions(url, name, intervalSeconds, 0, staleAfterSeconds, clock).subscribe(dispatch);
                return subscriptionListeners.addListener(name);
            }
        };

        subscriptions[name] = wrappedSubscription;

        return wrappedSubscription;
    },

    kick(name) {
        dispatch(subscriptionKicked(name));
        if (subscriptions[name]) {
            return subscriptions[name].kick();
        }
    }
});
